import { z } from 'zod'
import type { SupplierCategory } from './supplier'
import { planningTimeSchema } from './supplier-plan'

export const PHOTO_ROLES = ['photo', 'video'] as const
export const PHOTO_MOMENT_TYPES = [
  'bridePrep',
  'groomPrep',
  'firstLook',
  'ceremony',
  'groups',
  'couple',
  'firstDance',
  'party',
  'exit',
  'engagement',
  'afterWedding',
  'custom',
] as const
/** `weddingDay` moments happen on the wedding date; `other` ones are extra shoots on their own date. */
export const PHOTO_MOMENT_SCOPES = ['weddingDay', 'other'] as const
/** Supplier row ids of the former fixed slots; Harmonogram arrival events are keyed by them. */
export const LEGACY_PHOTO_SUPPLIER_IDS = ['photographer', 'video'] as const
/** Guests a group shot draws from; `all` means every guest. */
export const PHOTO_GROUP_SETS = ['all', 'bride-family', 'groom-family', 'friends'] as const
export const PHOTO_PRIORITIES = ['must', 'if-time'] as const
export const PHOTO_SUPPLIER_LIMIT = 20
export const PHOTO_MOMENT_LIMIT = 60
export const PHOTO_GROUP_LIMIT = 60
export const PHOTO_WISH_LIMIT = 100
export const PHOTO_GROUP_GUEST_LIMIT = 500
export const PHOTO_GROUP_MINUTES = { min: 1, max: 60, default: 3 } as const
export const PHOTO_HIDDEN_PROMPT_LIMIT = 100

export const photoRoleSchema = z.enum(PHOTO_ROLES)
export const photoMomentTypeSchema = z.enum(PHOTO_MOMENT_TYPES)
export type PhotoRole = z.infer<typeof photoRoleSchema>
export type PhotoMomentType = z.infer<typeof photoMomentTypeSchema>
export type PhotoMomentScope = (typeof PHOTO_MOMENT_SCOPES)[number]
export const photoGroupSetSchema = z.enum(PHOTO_GROUP_SETS)
export const photoPrioritySchema = z.enum(PHOTO_PRIORITIES)
export type PhotoGroupSet = z.infer<typeof photoGroupSetSchema>
export type PhotoPriority = z.infer<typeof photoPrioritySchema>

/** Supplier category offered when picking or creating the supplier of a role. */
export const photoRoleCategory = {
  photo: 'photographer',
  video: 'videographer',
} as const satisfies Record<PhotoRole, SupplierCategory>

/**
 * A wedding supplier fits a role when one of its categories or roles is the role's category or a
 * photo and video team, which covers both roles.
 */
export function supplierFitsPhotoRole(
  role: PhotoRole,
  supplier: { categories: readonly string[]; roles: readonly string[] },
) {
  const fitting: readonly string[] = [photoRoleCategory[role], 'photo-video-team']
  return [...supplier.categories, ...supplier.roles].some((job) => fitting.includes(job))
}

const photoSupplierRowSchema = z.object({
  id: z.uuid().or(z.enum(LEGACY_PHOTO_SUPPLIER_IDS)),
  weddingSupplierId: z.uuid().optional(),
  /** Snapshot of the supplier's name for the schedule and exports. */
  name: z.string().trim().max(300),
  role: photoRoleSchema,
  /** Why the row was added, such as a suggested second shooter; empty for ordinary rows. */
  purpose: z.string().trim().max(100).default(''),
  arrival: planningTimeSchema,
  end: planningTimeSchema,
})

const photoMomentSchema = z
  .object({
    id: z.uuid(),
    type: photoMomentTypeSchema,
    name: z.string().trim().max(300),
    scope: z.enum(PHOTO_MOMENT_SCOPES),
    /** Only extra shoots carry a date; empty means it is not known yet. */
    date: z.iso.date().or(z.literal('')),
    time: planningTimeSchema,
    timeEnd: planningTimeSchema,
    place: z.string().trim().max(300),
    /** Supplier row ids; ids of removed rows are dropped. */
    coveredBy: z.array(z.string()).max(PHOTO_SUPPLIER_LIMIT),
    guestVisible: z.boolean(),
    /** The ceremony moment takes its start and place from the ceremony plan while true. */
    followsCeremony: z.boolean().optional(),
    notes: z.string().trim().max(10_000),
  })
  .transform(({ followsCeremony, ...moment }) => ({
    ...moment,
    followsCeremony: followsCeremony ?? moment.type === 'ceremony',
  }))
  .refine((moment) => moment.scope === 'other' || moment.date === '', {
    path: ['date'],
    params: { reason: 'date-other-scope-only' },
  })
  .refine((moment) => !moment.followsCeremony || moment.type === 'ceremony', {
    path: ['followsCeremony'],
    params: { reason: 'follows-ceremony-type' },
  })
  .refine((moment) => !moment.followsCeremony || (!moment.time && !moment.place), {
    path: ['followsCeremony'],
    params: { reason: 'follows-ceremony-copy' },
  })

/** A group shot; its members are resolved from the guest list and the ceremony witnesses. */
export const photoGroupSchema = z
  .object({
    id: z.uuid(),
    name: z.string().trim().max(300),
    sets: z.array(photoGroupSetSchema).max(PHOTO_GROUP_SETS.length),
    /** Guests picked one by one; ids of removed guests stay until the planner removes them. */
    guestIds: z.array(z.uuid()).max(PHOTO_GROUP_GUEST_LIMIT),
    witnesses: z.boolean(),
    priority: photoPrioritySchema,
    note: z.string().trim().max(2000),
    /** The guest who calls the group together, or empty. */
    caller: z.uuid().or(z.literal('')),
    minutes: z
      .number()
      .int()
      .min(PHOTO_GROUP_MINUTES.min)
      .max(PHOTO_GROUP_MINUTES.max)
      .default(PHOTO_GROUP_MINUTES.default),
  })
  .transform((group) => ({
    ...group,
    sets: [...new Set(group.sets)],
    guestIds: [...new Set(group.guestIds)],
  }))

export const photoWishSchema = z.object({
  id: z.uuid(),
  text: z.string().trim().max(500),
  priority: photoPrioritySchema,
})

export const photoSchema = z
  .object({
    suppliers: z.array(photoSupplierRowSchema).max(PHOTO_SUPPLIER_LIMIT),
    videoUnneeded: z.boolean(),
    moments: z.array(photoMomentSchema).max(PHOTO_MOMENT_LIMIT),
    /** Plans stored before the shot list hold no groups or wishes. */
    groups: z.array(photoGroupSchema).max(PHOTO_GROUP_LIMIT).default([]),
    wishes: z.array(photoWishSchema).max(PHOTO_WISH_LIMIT).default([]),
    /** Long enough for the lines a converted legacy plan appends for its two suppliers. */
    notes: z.string().trim().max(20_000),
    /** Prompts the planner hid; the limit applies after removed rows' codes are dropped. */
    hiddenPromptCodes: z.array(z.string().trim().min(1).max(80)).default([]),
  })
  .refine(
    (plan) => {
      const ids = [...plan.suppliers, ...plan.moments].map((item) => item.id)
      return new Set(ids).size === ids.length
    },
    { path: ['moments'], params: { reason: 'duplicate-id' } },
  )
  /** Harmonogram has one Obřad, so the plan has at most one moment of it. */
  .refine((plan) => plan.moments.filter((moment) => moment.type === 'ceremony').length <= 1, {
    path: ['moments'],
    params: { reason: 'ceremony-moment-once' },
  })
  .refine(
    (plan) => {
      const ids = [...plan.groups, ...plan.wishes].map((item) => item.id)
      return new Set(ids).size === ids.length
    },
    { path: ['groups'], params: { reason: 'duplicate-id' } },
  )
  .transform((plan) => {
    const rowIds = new Set(plan.suppliers.map((row) => row.id))
    return {
      ...plan,
      moments: plan.moments.map((moment) => ({
        ...moment,
        coveredBy: [...new Set(moment.coveredBy)].filter((id) => rowIds.has(id)),
      })),
      hiddenPromptCodes: liveHiddenPromptCodes(plan.hiddenPromptCodes, rowIds),
    }
  })
  .refine((plan) => plan.hiddenPromptCodes.length <= PHOTO_HIDDEN_PROMPT_LIMIT, {
    path: ['hiddenPromptCodes'],
    params: { reason: 'too-many' },
  })
export type PhotoInput = z.infer<typeof photoSchema>
export type PhotoSupplierRow = PhotoInput['suppliers'][number]
export type PhotoMoment = PhotoInput['moments'][number]
export type PhotoGroup = PhotoInput['groups'][number]
export type PhotoWish = PhotoInput['wishes'][number]

/** Hidden codes without duplicates; codes starting `arrival-` name a supplier row and go with it. */
function liveHiddenPromptCodes(codes: readonly string[], rowIds: ReadonlySet<string>) {
  return [...new Set(codes)].filter(
    (code) => !code.startsWith('arrival-') || rowIds.has(code.slice('arrival-'.length)),
  )
}

export function emptyPhoto(): PhotoInput {
  return {
    suppliers: [],
    videoUnneeded: false,
    moments: [],
    groups: [],
    wishes: [],
    notes: '',
    hiddenPromptCodes: [],
  }
}
